import { formatDate, relativeTime } from '@/utils/date'
import { useMemo, useCallback, useState } from 'react'
import * as store from '@store'
import * as actions from '@actions'
import { BsEyeFill, BsBookmarkCheckFill, BsFillClockFill, BsCheckLg } from 'react-icons/bs'
import { MdDownload, MdMenuBook, MdModeEdit } from 'react-icons/md'
import { createSyntheticDownload } from '@/utils'
import { useWireValue } from '@forminator/react-wire'
import PaidFeatureBadge from '@/components/shared/PaidFeatureBadge'
import useDocumentPopupHandler from '@hook/useDocumentPopupHandler'
import { DOCUMENT_STATUS_UPLOADED, hasNoFile } from '@/constants'
import CustomModal from '@/components/shared/CustomModal/CustomModal'
import { toast } from 'react-toastify'
import DocumentOcrPage from './DocumentOcrPage'


const dateFormat = 'MM/DD/YYYY'

const fieldNames = [
    'fullGrantor',
    'fullGrantee',
    'grantors',
    'grantees',
    'bookVolume',
    'bookPage',
    'bookType',
    'instrumentNumber',
    'instrumentType',
    'documentType',
    'instrumentDate',
    'instrumentDateRaw',
    'recordingDate',
    'recordingDateRaw',
    'fileDate',
    'fileDateRaw',
    'legalDescriptions',
    'comments',
]

const fieldKeys = [
    'name',
    'grantors',
    'grantees',
    'bookVolume',
    'bookPage',
    'bookType',
    'instrumentNumber',
    'instrumentType',
    'instrumentDate',
    'fileDate',
    'recordingDate',
    'legalDescriptions',
    'comments',
]

const DocumentRow = ({
    doc,
    useSortedFields = true,
    rsDocJoin,
    markedDocuments,
    setMarkedDocuments,
}) => {
    // if its indexView, then show the icons, it shouldn't show on the runsheet
    const isIndexView = !!doc
    
    const [showOcr, setShowOcr] = useState(false)
    
    const record = useMemo(() => doc || rsDocJoin?.document || {}, [doc, rsDocJoin])
    const values = useMemo(() => doc?.values?.[0] || rsDocJoin?.value || {}, [doc, rsDocJoin])
    const rawOcrDataArray = useMemo(() => record?.pages?.map(page => (
        <DocumentOcrPage key={page.id} page={page} length={record.pages.length} />
    )) || [], [record])
    
    const remoteConfig = useWireValue(store.remoteConfig)
    const viewedDocuments = useWireValue(store.viewedDocuments)
    const runsheet = useWireValue(store.currentRunsheet)
    const documentHeaders = useWireValue(store.documentHeaders)
    const runsheetHeaders = useWireValue(store.runsheetHeaders)
    
    const fieldsToShow = useMemo(() => {
        if (isIndexView)
            return useSortedFields
                ? (documentHeaders.length ? documentHeaders : fieldKeys)
                : fieldKeys
        return useSortedFields
            ? (runsheetHeaders.length ? ['name', ...runsheetHeaders] : fieldKeys)
            : fieldKeys
    }, [fieldKeys, documentHeaders, runsheetHeaders])
    
    const checkDoc = useCallback(e => 
        setMarkedDocuments(e.target.checked
            ? [...markedDocuments, record.id]
            : markedDocuments.filter(id => id !== record.id),
            
        )
    , [record, markedDocuments])
    
    const nameFormatting = value => {
        try {
            return value?.split('%,%')?.join(' ') ?? ''
        } catch (e) {
            return value || ''
        }
    }
    
    const fields = useMemo(() => fieldNames.reduce((acc, it) => ({
        ...acc,
        [it]: values?.[it] ?? '',
    }), {}), [values])
    
    const { popupDocumentOrNavigate } = useDocumentPopupHandler()
    
    const isInTheRunsheet = useCallback(documentId => (
        runsheet?.rdj?.some(rd => rd.documentId === documentId)
    ), [runsheet?.rdj])
    
    const download = async documentId => {
        const res = await actions.downloadDocumentPdf(documentId)
        
        try {
            createSyntheticDownload(res.data, res.headers['content-type'], res.headers['content-name'])
        } catch {
            toast.error('Failed to download document.')
        }
    }
    
    const mergedLegalDescriptions = useMemo(() => {
        
        const items = Array.isArray(fields?.legalDescriptions) ? fields?.legalDescriptions : []
        
        return items.filter(it => it.visible === true).map(it => it.summary)
        
    }, [fields])
    
    const columnMappings = {
        name: <td key="name"
            data-tooltip-id="default-tip"
            data-tooltip-content={record.name}>
            <div className="line-clamp-4">
                {record.name}
            </div>
        </td>,
        
        grantors: (isIndexView
            ? <td key="grantors"
                data-tooltip-id="default-tip"
                data-tooltip-content={nameFormatting(fields?.fullGrantor)}>
                <div className="line-clamp-4">
                    {nameFormatting(fields?.fullGrantor)}
                </div>
            </td>
            : <td key="grantors"
                data-tooltip-id="default-tip"
                data-tooltip-content={nameFormatting(fields?.grantors?.map(g => g.name).join('; '))}>
                <div className="line-clamp-4">
                    {nameFormatting(fields?.grantors?.map(g => g.name).join('; '))}
                </div>
            </td>),
        
        grantees: (isIndexView
            ? <td key="grantees"
                data-tooltip-id="default-tip"
                data-tooltip-content={nameFormatting(fields?.fullGrantee)}>
                <div className="line-clamp-4">
                    {nameFormatting(fields?.fullGrantee)}
                </div>
            </td>
            : <td key="grantees"
                data-tooltip-id="default-tip"
                data-tooltip-content={nameFormatting(fields?.grantees?.map(g => g.name).join('; '))}>
                <div className="line-clamp-4">
                    {nameFormatting(fields?.grantees?.map(g => g.name).join('; '))}
                </div>
            </td>),
        
        bookVolume: <td key="bookVolume"
            data-tooltip-id="default-tip"
            data-tooltip-content={fields?.bookVolume}>
            <div className="line-clamp-4">
                {fields?.bookVolume}
            </div>
        </td>,
        
        bookPage: <td key="bookPage"
            data-tooltip-id="default-tip"
            data-tooltip-content={fields?.bookPage}>
            <div className="line-clamp-4">
                {fields?.bookPage}
            </div>
        </td>,
        
        bookType: <td key="bookType"
            data-tooltip-id="default-tip"
            data-tooltip-content={fields?.bookType}>
            <div className="line-clamp-4">
                {fields?.bookType}
            </div>
        </td>,
        
        instrumentNumber: <td key="instrumentNumber"
            data-tooltip-id="default-tip"
            data-tooltip-content={fields?.instrumentNumber}>
            <div className="line-clamp-4">
                {fields?.instrumentNumber}
            </div>
        </td>,
        
        instrumentType: <td key="instrumentType"
            data-tooltip-id="default-tip"
            data-tooltip-content={fields?.instrumentType}>
            <div className="line-clamp-4"> 
                {fields?.instrumentType}
            </div>
        </td>,
        
        instrumentDate: <td key="instrumentDate"
            data-tooltip-id="default-tip"
            data-tooltip-content={formatDate(fields?.instrumentDate, dateFormat)}>
            <div className="line-clamp-4">
                {fields?.instrumentDate ? formatDate(fields?.instrumentDate, dateFormat) : fields?.instrumentDateRaw}
            </div>
        </td>,
        
        fileDate: <td key="fileDate"
            data-tooltip-id="default-tip"
            data-tooltip-content={formatDate(fields?.fileDate, dateFormat)}>
            <div className="line-clamp-4"> 
                {fields?.fileDate ? formatDate(fields?.fileDate, dateFormat) : fields?.fileDateRaw}
            </div>
        </td>,
        
        recordingDate: !isIndexView
            ? <td key="recordingDate"
                data-tooltip-id="default-tip"
                data-tooltip-content={formatDate(fields?.recordingDate, dateFormat)}>
                <div className="line-clamp-4"> 
                    {fields?.recordingDate ? formatDate(fields?.recordingDate, dateFormat) : fields?.recordingDateRaw}
                </div>
            </td>
            : null,
        
        legalDescriptions: (isIndexView
            ? <td key="legalDescriptions"
                data-tooltip-id="default-tip"
                data-tooltip-content={fields?.allLegalDescriptions}>
                <div className="line-clamp-4">
                    {/* if its all legal descriptions from index value, show this one */}
                    {fields?.allLegalDescriptions}
                </div>
            </td>
            : <td key="legalDescriptions"
                data-tooltip-id="default-tip"
                data-tooltip-content={mergedLegalDescriptions.join(' ')}>
                <div className="line-clamp-4">
                    {/* it can be legals from elastic or from runsheet,
                        either way, if there's "visible" prop, it should filter from it */}
                    {mergedLegalDescriptions.map(it =>
                        <div key={it.id} className="pt-2">
                            {it}
                        </div>,
                    )}
                </div>
            </td>),
        comments: (isIndexView
            ? null
            :
            <td key="comments"
                data-tooltip-id="default-tip"
                data-tooltip-content={fields?.comments}>
                <div className="line-clamp-4">
                    {fields?.comments}
                </div>
            </td>),
    }
    
    return (
        <>
            {showOcr && (
                <CustomModal 
                    open={showOcr}
                    modalHandler={() => setShowOcr(false)}>
                    <h1>{record.name}</h1>
                    {rawOcrDataArray}
                </CustomModal>
            )}
            <tr>
                
                <td>
                    <div className="flex flex-col justify-center">
                        <input
                            type="checkbox"
                            className="checkbox-tract"
                            checked={markedDocuments.includes(record.id)}
                            onChange={checkDoc} />
                    </div>
                </td>
                
                <td>
                    <div className="flex gap-2"
                        data-tooltip-id="default-tip"
                        data-tooltip-content={hasNoFile(record)
                            ? 'Index record only. No document record is available to View'
                            : ''}>
                        <button
                            className="view-document-btn p-2 btn btn-primary btn-sm btn-square"
                            disabled={hasNoFile(record) || record.reviewStatus === DOCUMENT_STATUS_UPLOADED}
                            onClick={popupDocumentOrNavigate(record)}
                            rel="noreferrer">
                            <BsEyeFill data-tooltip-id="default-tip" data-tooltip-content="View" />
                            <PaidFeatureBadge show={remoteConfig.payments.enabled &&
                                record.collection?.isParse && !record.purchased} />
                        </button>
                        <a
                            className="annotate-document-btn p-2 btn btn-primary btn-sm btn-square"
                            target="_blank"
                            onClick={() => !hasNoFile(record) && actions.addDocumentToHistory(record)}
                            href={`/edit/${record.id}`}
                            rel="noreferrer">
                            <MdModeEdit data-tooltip-id="default-tip" data-tooltip-content="Edit" />
                        </a>
                        <a
                            href="#"
                            className="download-document-btn p-2 btn btn-primary btn-sm btn-square"
                            onClick={() => !hasNoFile(record) && download(record.id)}>
                            <MdDownload data-tooltip-id="default-tip" data-tooltip-content="Download" />
                        </a>
                        {rawOcrDataArray.length > 0 && isIndexView && (
                            <button
                                className="p-2 btn btn-secondary btn-sm btn-square"
                                onClick={() => setShowOcr(true)}>
                                <MdMenuBook data-tooltip-id="default-tip" data-tooltip-content="View Document Scan"/>
                            </button>
                        )}
                    </div>
                    {isIndexView && (!!viewedDocuments[record.id] || isInTheRunsheet(record.id)) && (
                        <div className="flex justify-around p-2">
                            {!!viewedDocuments[record.id] && (
                                <BsFillClockFill
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content={
                                        `Viewed ${relativeTime(viewedDocuments[record.id].viewedAt)}`} />
                            )}
                            {isInTheRunsheet(record.id) && (
                                <BsBookmarkCheckFill
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="Document present in the current Runsheet"/>
                            )}
                            {record.annotationCompleted && (
                                <BsCheckLg
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="Annotations Marked Complete"/>
                            )}
                        </div>
                    )}
                </td>
                
                {fieldsToShow.map(f => columnMappings[f])}
            
            </tr>
        
        </>
        
    )
    
}

export default DocumentRow
